
import React, { useCallback, useMemo, useState } from 'react';

import { useDebouncedEffect } from '../../hooks/useDebouncedEffect';

import { getInstitutionsByLink } from '../../services/Institutions';
import { getCoeApplicationsExpired } from '../../services/CoeApplications';
import { getDepositApplicationsExpired } from '../../services/DepositApplications';
import { getCommittedApplicationsExpired } from '../../services/CommittedApplications';

import { useWorkspaceContext } from '../workspace';

import { CoeApplications } from './CoeApplications';
import { OfflineInstitutions } from './OfflineInstitutions';
import { DepositApplications } from './DepositApplications';
import { CommittedApplications } from './CommittedApplications';

export const useSystemNotificationsProvider = () => {
  const [toasts, setToasts] = useState([]);
  const { workspace: { defaultWorkspace } } = useWorkspaceContext();

  const addToast = (toast) => {
    const key = toast.key || Date.now();

    if (toasts.find((toastEl) => toastEl.key === key)) {
      return;
    }

    const rest = toasts.length < 3 ? toasts : toasts.slice(0, -1);

    setToasts([{ ...toast, key }, ...rest]);
  };

  const removeToast = (key) => {
    setToasts((prev) => prev.filter((toast) => toast.key !== key));
  };

  const value = useMemo(() => ({
    toasts,
    setToasts,
    addToast,
    removeToast,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [toasts]);

  const depositApplicationsExpiredMsg = useCallback((el) => ({
    key: `deposit-applications-expired-${el.currency}`,
    children: <DepositApplications currency={el.currency} amount={el.amount} />,
  }), []);

  const coeApplicationsExpiredMsg = useCallback((el) => ({
    key: `coe-applications-expired-${el.currency}`,
    children: <CoeApplications currency={el.currency} amount={el.amount} />,
  }), []);

  const committedApplicationsExpiredMsg = useCallback((el) => ({
    key: `committed-applications-expired-${el.currency}`,
    children: <CommittedApplications currency={el.currency} amount={el.amount} />,
  }), []);

  const offlineInstitutionsMsg = useCallback(({ id, description }) => ({
    key: `offline-institutions-${id}`,
    children: <OfflineInstitutions id={id} description={description} />,
  }), []);

  useDebouncedEffect(() => {
    const init = async () => {
      try {
        const [
          depositApplicationsExpired,
          committedApplicationsExpired,
          coeApplicationsExpired,
          offlineInstitutions,
        ] = await Promise.all([
          getDepositApplicationsExpired(),
          getCommittedApplicationsExpired(),
          getCoeApplicationsExpired(),
          getInstitutionsByLink(),
        ]);

        const coe = coeApplicationsExpired.filter(({ amount }) => amount > 0);
        const deposit = depositApplicationsExpired.filter(({ amount }) => amount > 0);
        const committed = committedApplicationsExpired.filter(({ amount }) => amount > 0);

        const newToasts = [
          ...(offlineInstitutions.length > 0 ? offlineInstitutions.map(offlineInstitutionsMsg) : []),
          ...(deposit.length > 0 ? deposit.map(depositApplicationsExpiredMsg) : []),
          ...(committed.length > 0 ? committed.map(committedApplicationsExpiredMsg) : []),
          ...(coe.length > 0 ? committed.map(coeApplicationsExpiredMsg) : []),
        ];

        setToasts(newToasts);
      } catch (_error) {
        // console.log(error);
      }
    };

    if (defaultWorkspace.id) init();
  }, [defaultWorkspace.id], 500);

  return value;
};
